import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Pencil, Trash2, Calculator, Info } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { handleError, handleSuccess } from "@/lib/error-handler";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type PricingType = "fixed" | "per_unit" | "per_char" | "per_area";

const pricingTypeLabels: Record<PricingType, string> = {
  fixed: "Kiinteä hinta",
  per_unit: "Per yksikkö",
  per_char: "Per merkki",
  per_area: "Per neliö",
};

const pricingTypeDescriptions: Record<PricingType, string> = {
  fixed: "Kiinteä lisähinta komponentille",
  per_unit: "Hinta kerrottuna määrällä",
  per_char: "Hinta kerrottuna tekstin merkkimäärällä",
  per_area: "Hinta kerrottuna pinta-alalla (leveys × korkeus)",
};

const priceRuleSchema = z.object({
  name: z.string().min(1, "Nimi on pakollinen"),
  description: z.string().optional(),
  product_model_id: z.string().optional(),
  component_type_id: z.string().optional(),
  material_id: z.string().optional(),
  attribute_id: z.string().optional(),
  pricing_type: z.enum(["fixed", "per_unit", "per_char", "per_area"]),
  base_amount: z.number().min(0),
  unit_price: z.number().min(0),
  multiplier_attribute_id: z.string().optional(),
  is_active: z.boolean(),
  priority: z.number().int().min(0),
});

type PriceRuleFormData = z.infer<typeof priceRuleSchema>;

interface PriceRule {
  id: string;
  name: string;
  description: string | null;
  product_model_id: string | null;
  component_type_id: string | null;
  material_id: string | null;
  attribute_id: string | null;
  pricing_type: PricingType;
  base_amount: number;
  unit_price: number;
  multiplier_attribute_id: string | null;
  is_active: boolean;
  priority: number;
  product_models?: { name: string } | null;
  component_types?: { label: string } | null;
  materials?: { name: string } | null;
  attributes?: { label: string } | null;
}

export default function PricingRules() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PriceRule | null>(null);

  const form = useForm<PriceRuleFormData>({
    resolver: zodResolver(priceRuleSchema),
    defaultValues: {
      name: "",
      description: "",
      pricing_type: "fixed",
      base_amount: 0,
      unit_price: 0,
      is_active: true,
      priority: 0,
    },
  });

  const { data: priceRules, isLoading } = useQuery({
    queryKey: ["price-rules"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("price_rules")
        .select(`
          *,
          product_models (name),
          component_types (label),
          materials (name)
        `)
        .is("deleted_at", null)
        .order("priority", { ascending: true });

      if (error) throw error;
      
      // Fetch attribute labels separately due to ambiguous relationship
      const attrIds = [...new Set(data?.map(d => d.attribute_id).filter(Boolean))];
      let attrMap: Record<string, { label: string }> = {};
      
      if (attrIds.length > 0) {
        const { data: attrs } = await supabase
          .from("attributes")
          .select("id, label")
          .in("id", attrIds);
        attrs?.forEach(a => { attrMap[a.id] = { label: a.label }; });
      }
      
      return data?.map(d => ({
        ...d,
        attributes: d.attribute_id ? attrMap[d.attribute_id] || null : null
      })) as PriceRule[];
    },
  });

  const { data: productModels } = useQuery({
    queryKey: ["product-models-for-pricing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_models")
        .select("id, name")
        .is("deleted_at", null)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: componentTypes } = useQuery({
    queryKey: ["component-types-for-pricing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("component_types")
        .select("id, label")
        .is("deleted_at", null)
        .order("label");
      if (error) throw error;
      return data;
    },
  });

  const { data: materials } = useQuery({
    queryKey: ["materials-for-pricing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("materials")
        .select("id, name")
        .is("deleted_at", null)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: attributes } = useQuery({
    queryKey: ["attributes-for-pricing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attributes")
        .select("id, label, attribute_type")
        .is("deleted_at", null)
        .order("label");
      if (error) throw error;
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: PriceRuleFormData) => {
      const payload = {
        name: data.name,
        description: data.description || null,
        product_model_id: data.product_model_id || null,
        component_type_id: data.component_type_id || null,
        material_id: data.material_id || null,
        attribute_id: data.attribute_id || null,
        pricing_type: data.pricing_type,
        base_amount: data.base_amount,
        unit_price: data.unit_price,
        multiplier_attribute_id: data.multiplier_attribute_id || null,
        is_active: data.is_active,
        priority: data.priority,
      };

      if (editingRule) {
        const { error } = await supabase
          .from("price_rules")
          .update(payload)
          .eq("id", editingRule.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("price_rules").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["price-rules"] });
      handleSuccess(editingRule ? "Hintasääntö päivitetty" : "Hintasääntö luotu");
      handleClose();
    },
    onError: (error) => {
      handleError(error, { title: "Virhe tallennuksessa" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("price_rules")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["price-rules"] });
      handleSuccess("Hintasääntö poistettu");
    },
    onError: (error) => {
      handleError(error, { title: "Virhe poistossa" });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("price_rules")
        .update({ is_active })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["price-rules"] });
    },
    onError: (error) => {
      handleError(error, { title: "Virhe päivityksessä" });
    },
  });

  const handleEdit = (rule: PriceRule) => {
    setEditingRule(rule);
    form.reset({
      name: rule.name,
      description: rule.description || "",
      product_model_id: rule.product_model_id || undefined,
      component_type_id: rule.component_type_id || undefined,
      material_id: rule.material_id || undefined,
      attribute_id: rule.attribute_id || undefined,
      pricing_type: rule.pricing_type,
      base_amount: rule.base_amount,
      unit_price: rule.unit_price,
      multiplier_attribute_id: rule.multiplier_attribute_id || undefined,
      is_active: rule.is_active,
      priority: rule.priority,
    });
    setIsDialogOpen(true);
  };

  const handleNew = () => {
    setEditingRule(null);
    form.reset({
      name: "",
      description: "",
      pricing_type: "fixed",
      base_amount: 0,
      unit_price: 0,
      is_active: true,
      priority: 0,
    });
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditingRule(null);
    form.reset();
  };

  const onSubmit = (data: PriceRuleFormData) => {
    saveMutation.mutate(data);
  };

  const getRuleScopeDescription = (rule: PriceRule) => {
    const parts: string[] = [];
    if (rule.product_models?.name) parts.push(`Malli: ${rule.product_models.name}`);
    if (rule.component_types?.label) parts.push(`Komponentti: ${rule.component_types.label}`);
    if (rule.materials?.name) parts.push(`Materiaali: ${rule.materials.name}`);
    if (rule.attributes?.label) parts.push(`Attribuutti: ${rule.attributes.label}`);
    return parts.length > 0 ? parts.join(" • ") : "Yleinen sääntö";
  };

  const getPricePreview = (rule: PriceRule) => {
    if (rule.pricing_type === "fixed") {
      return `${rule.base_amount.toFixed(2)} €`;
    } else if (rule.pricing_type === "per_unit") {
      return `${rule.base_amount.toFixed(2)} € + ${rule.unit_price.toFixed(2)} €/kpl`;
    } else if (rule.pricing_type === "per_char") {
      return `${rule.base_amount.toFixed(2)} € + ${rule.unit_price.toFixed(2)} €/merkki`;
    } else if (rule.pricing_type === "per_area") {
      return `${rule.base_amount.toFixed(2)} € + ${rule.unit_price.toFixed(2)} €/m²`;
    }
    return "-";
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Hintasäännöt</h1>
            <p className="text-muted-foreground mt-1">
              Hallitse tuotteiden, komponenttien ja materiaalien hinnoittelua
            </p>
          </div>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4 mr-2" />
            Lisää hintasääntö
          </Button>
        </div>

        {/* Pricing Explanation Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Hinnoittelulogiikka
            </CardTitle>
            <CardDescription>
              Säännöt käsitellään prioriteettijärjestyksessä (pienempi numero = korkeampi prioriteetti).
              Lopullinen hinta koostuu perushinnasta ja yksiköistä riippuvista lisistä.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(Object.keys(pricingTypeLabels) as PricingType[]).map((type) => (
                <div key={type} className="p-3 bg-muted rounded-lg">
                  <div className="font-medium text-sm">{pricingTypeLabels[type]}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {pricingTypeDescriptions[type]}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Price Rules Table */}
        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Ladataan...</div>
            ) : !priceRules || priceRules.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>Ei hintasääntöjä. Lisää ensimmäinen sääntö.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">Prior.</TableHead>
                    <TableHead>Nimi</TableHead>
                    <TableHead>Kohdistus</TableHead>
                    <TableHead>Tyyppi</TableHead>
                    <TableHead className="text-right">Hinta</TableHead>
                    <TableHead className="w-[100px]">Aktiivinen</TableHead>
                    <TableHead className="w-[100px]">Toiminnot</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {priceRules.map((rule) => (
                    <TableRow key={rule.id} className={!rule.is_active ? "opacity-50" : ""}>
                      <TableCell>
                        <Badge variant="outline">{rule.priority}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{rule.name}</div>
                          {rule.description && (
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {rule.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {getRuleScopeDescription(rule)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{pricingTypeLabels[rule.pricing_type]}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {getPricePreview(rule)}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={rule.is_active}
                          onCheckedChange={(checked) =>
                            toggleActiveMutation.mutate({ id: rule.id, is_active: checked })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(rule)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (confirm("Haluatko varmasti poistaa tämän hintasäännön?")) {
                                deleteMutation.mutate(rule.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Price Rule Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRule ? "Muokkaa hintasääntöä" : "Lisää hintasääntö"}
              </DialogTitle>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nimi *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="esim. Graniitti-lisä" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          Prioriteetti
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-3 w-3" />
                            </TooltipTrigger>
                            <TooltipContent>
                              Pienempi numero = käsitellään ensin
                            </TooltipContent>
                          </Tooltip>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Kuvaus</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={2} placeholder="Lisätietoja säännöstä..." />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="product_model_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tuotemalli</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Kaikki mallit" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="">Kaikki mallit</SelectItem>
                            {productModels?.map((model) => (
                              <SelectItem key={model.id} value={model.id}>
                                {model.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="component_type_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Komponenttityyppi</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Kaikki komponentit" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="">Kaikki komponentit</SelectItem>
                            {componentTypes?.map((ct) => (
                              <SelectItem key={ct.id} value={ct.id}>
                                {ct.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="material_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Materiaali</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Kaikki materiaalit" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="">Kaikki materiaalit</SelectItem>
                            {materials?.map((material) => (
                              <SelectItem key={material.id} value={material.id}>
                                {material.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="attribute_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Attribuutti</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Kaikki attribuutit" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="">Ei attribuuttia</SelectItem>
                            {attributes?.map((attr) => (
                              <SelectItem key={attr.id} value={attr.id}>
                                {attr.label} ({attr.attribute_type})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="pricing_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hinnoittelutyyppi *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(Object.keys(pricingTypeLabels) as PricingType[]).map((type) => (
                            <SelectItem key={type} value={type}>
                              <div>
                                <div>{pricingTypeLabels[type]}</div>
                                <div className="text-xs text-muted-foreground">
                                  {pricingTypeDescriptions[type]}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="base_amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Perushinta (€)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormDescription>Lisätään aina kokonaishintaan</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="unit_price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Yksikköhinta (€)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormDescription>
                          {form.watch("pricing_type") === "per_char"
                            ? "Kerrotaan merkkimäärällä"
                            : form.watch("pricing_type") === "per_area"
                            ? "Kerrotaan pinta-alalla (m²)"
                            : form.watch("pricing_type") === "per_unit"
                            ? "Kerrotaan määrällä"
                            : "Ei käytössä kiinteässä hinnassa"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {form.watch("pricing_type") !== "fixed" && (
                  <FormField
                    control={form.control}
                    name="multiplier_attribute_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Kerroin-attribuutti</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Valitse attribuutti kertoimen lähteeksi" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="">Oletus (määrä/teksti)</SelectItem>
                            {attributes
                              ?.filter((a) => a.attribute_type === "number" || a.attribute_type === "text")
                              .map((attr) => (
                                <SelectItem key={attr.id} value={attr.id}>
                                  {attr.label}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Valitse mistä attribuutista kerroin luetaan (esim. tekstin pituus)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel>Aktiivinen</FormLabel>
                        <FormDescription>
                          Vain aktiiviset säännöt vaikuttavat hinnoitteluun
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleClose}>
                    Peruuta
                  </Button>
                  <Button type="submit" disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? "Tallennetaan..." : "Tallenna"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
